import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader, PageContainer } from '@/components/layout/PageHeader'
import { Badge, Card, CardContent } from '@/components/ui'
import { adminApi } from '@/modules/admin/api/admin.api'

const RESOURCE_LABELS: Record<string, string> = {
  users: 'Utilisateurs', rbac: 'Rôles & Permissions', accounting: 'Comptabilité',
  treasury: 'Trésorerie', hr: 'RH', payroll: 'Paie', leaves: 'Congés',
  attendance: 'Pointage', projects: 'Projets', tasks: 'Tâches', chat: 'Chat',
  documents: 'Documents', meetings: 'Réunions', crm: 'CRM', tickets: 'Tickets',
  reports: 'Rapports', config: 'Configuration', audit: 'Audit',
}

// Traductions des actions en français
const ACTION_LABELS: Record<string, string> = {
  view: 'Voir', create: 'Créer', update: 'Modifier', delete: 'Supprimer',
  manage: 'Gérer', approve: 'Approuver', reject: 'Rejeter', cancel: 'Annuler',
  export: 'Exporter', import: 'Importer', validate: 'Valider', archive: 'Archiver',
  restore: 'Restaurer', assign: 'Assigner', unassign: 'Désassigner',
  start: 'Démarrer', complete: 'Terminer', configure: 'Configurer',
  view_all: 'Voir tout', view_own: 'Voir les siens', view_department: 'Voir dép.',
  create_account: 'Créer compte', approve_account: 'Approuver compte',
  close: 'Clôturer', issue: 'Émettre', send: 'Envoyer', pay: 'Payer',
  read: 'Lire', write: 'Écrire', admin: 'Administrer',
  '*': 'Tous droits',
}


export default function PermissionsMatrixPage() {
  const [filter, setFilter] = useState('')

  const { data: roles } = useQuery({
    queryKey: ['admin-roles-full'],
    queryFn: () => adminApi.getRoles({ page: 1, limit: 100 }),
  })

  const { data: permissionsGrouped } = useQuery({
    queryKey: ['admin-permissions'],
    // FIX: getPermissionsByResource() retourne déjà { module: [...] } groupé — pas un tableau
    queryFn: () => adminApi.getPermissions(),
  })

  const { data: rolesWithPerms } = useQuery({
    queryKey: ['admin-roles-permissions'],
    queryFn: async () => {
      const list = await adminApi.getRoles({ page: 1, limit: 100 })
      const details = await Promise.all(
        list.data.map((r: any) => adminApi.getRoleById(r.id).catch(() => r))
      )
      return details
    },
  })

  // FIX: la réponse est déjà groupée par module { module: [Permission] }
  // On adapte pour avoir le même format attendu par le reste du composant
  const grouped = useMemo(() => {
    if (!permissionsGrouped) return {}
    // Si c'est déjà un objet groupé (réponse backend)
    if (!Array.isArray(permissionsGrouped)) {
      return permissionsGrouped as Record<string, any[]>
    }
    // Fallback si tableau
    return (permissionsGrouped as any[]).reduce<Record<string, any[]>>((acc, p) => {
      const key = p.resource ?? p.module ?? 'other'
      if (!acc[key]) acc[key] = []
      acc[key].push(p)
      return acc
    }, {})
  }, [permissionsGrouped])

  // Map roleId → Set<permissionId>
  const rolePermMap = useMemo(() => {
    const map = new Map<string, Set<string>>()
    rolesWithPerms?.forEach((role: any) => {
      // FIX: backend retourne rolePermissions[].permission, pas permissions[]
      const permIds = role.rolePermissions?.map((rp: any) => rp.permission?.id ?? rp.permissionId) ?? []
      map.set(role.id, new Set(permIds))
    })
    return map
  }, [rolesWithPerms])

  const filteredResources = Object.entries(grouped).filter(([resource]) =>
    filter ? resource.toLowerCase().includes(filter.toLowerCase()) ||
      (RESOURCE_LABELS[resource] ?? '').toLowerCase().includes(filter.toLowerCase())
      : true
  )

  const roleList = roles?.data ?? []

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="Matrice des Permissions"
        description="Vue globale des permissions par rôle"
        breadcrumb={[{ label: 'Administration' }, { label: 'Permissions' }]}
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Rôles', value: roleList.length, color: 'text-[var(--blue-d)]' },
          { label: 'Permissions', value: permissionsGrouped ? Object.values(permissionsGrouped as Record<string, any[]>).reduce((s, arr) => s + arr.length, 0) : 0, color: 'text-[var(--green-text)]' },
          { label: 'Ressources', value: Object.keys(grouped).length, color: 'text-[var(--mauve)]' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardContent className="py-4">
              <p className="text-xs text-[var(--tx3)] mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtre */}
      <div className="mb-4">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filtrer par ressource..."
          className="w-full max-w-xs bg-white border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--tx)] focus:outline-none focus:border-blue-500"
        />
      </div>

      {/* Tableau matrice */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider sticky left-0 bg-white z-10 min-w-[200px]">
                  Ressource / Action
                </th>
                {roleList.map((role: any) => (
                  <th key={role.id} className="px-3 py-3 text-center min-w-[120px]">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-xs font-medium text-[var(--tx)] truncate max-w-[110px]">{role.name}</span>
                      <Badge variant={role.isSystem ? 'purple' : 'info'} size="sm">
                        {role.isSystem ? 'Sys' : 'Custom'}
                      </Badge>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredResources.map(([resource, perms]) => (
                <>
                  {/* Ligne titre resource */}
                  <tr key={`header-${resource}`} className="bg-[var(--surface)]">
                    <td colSpan={roleList.length + 1} className="px-4 py-2">
                      <span className="text-xs font-semibold text-[var(--tx2)] uppercase tracking-wider">
                        {RESOURCE_LABELS[resource] ?? resource}
                      </span>
                    </td>
                  </tr>
                  {/* Lignes permissions */}
                  {perms.map((perm) => (
                    <tr key={perm.id} className="border-b border-[var(--bd)]/40 hover:bg-[var(--surface-3)]">
                      <td className="px-4 py-2.5 sticky left-0 bg-white z-10">
                        <span className="text-xs text-[var(--tx3)] font-mono">{perm.action}</span>
                        {perm.description && (
                          <p className="text-xs text-[var(--tx4)] mt-0.5 truncate max-w-[180px]">{perm.description}</p>
                        )}
                      </td>
                      {roleList.map((role: any) => {
                        const hasPerms = rolePermMap.get(role.id)
                        const has = hasPerms?.has(perm.id) ?? false
                        return (
                          <td key={role.id} className="px-3 py-2.5 text-center">
                            {has ? (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500/20 text-[var(--green-text)] text-xs">✓</span>
                            ) : (
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white text-[var(--tx4)] text-xs">—</span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>

          {filteredResources.length === 0 && (
            <div className="py-12 text-center text-[var(--tx3)] text-sm">Aucune ressource correspondante</div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
